<template>
  <section class="support-tickets-section">
    <div class="section-header">
      <h2 class="section-title">Support Tickets</h2>
      <div class="filter-actions">
        <select v-model="priorityFilter" class="filter-select">
          <option value="all">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
        <select v-model="statusFilter" class="filter-select ml-2">
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
    </div>
    
    <div class="tickets-overview">
      <div class="ticket-stat-card">
        <span class="stat-value">{{ ticketStats.total }}</span>
        <span class="stat-label">Total Tickets</span>
      </div>
      <div class="ticket-stat-card">
        <span class="stat-value">{{ ticketStats.open }}</span>
        <span class="stat-label">Open</span>
      </div>
      <div class="ticket-stat-card">
        <span class="stat-value">{{ ticketStats.in_progress }}</span>
        <span class="stat-label">In Progress</span>
      </div>
      <div class="ticket-stat-card">
        <span class="stat-value">{{ ticketStats.resolved }}</span>
        <span class="stat-label">Resolved</span>
      </div>
      <div class="ticket-stat-card">
        <span class="stat-value">{{ ticketStats.average_time }}h</span>
        <span class="stat-label">Avg. Response</span>
      </div>
    </div>
    
    <div class="tickets-table-container">
      <table class="tickets-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>User</th>
            <th>Subject</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ticket in filteredTickets" :key="ticket.id" class="ticket-row">
            <td class="ticket-id">#{{ ticket.id }}</td>
            <td class="ticket-user">
              <div class="user-info">
                <div class="user-avatar">{{ ticket.user.name.charAt(0) }}</div>
                <span>{{ ticket.user.name }}</span>
              </div>
            </td>
            <td class="ticket-subject">{{ ticket.subject }}</td>
            <td class="ticket-priority">
              <span class="priority-badge" :class="`priority-${ticket.priority}`">
                {{ ticket.priority }}
              </span>
            </td>
            <td class="ticket-status">
              <span class="status-badge" :class="`status-${ticket.status}`">
                {{ formatStatus(ticket.status) }}
              </span>
            </td>
            <td class="ticket-created">{{ formatDate(ticket.created_at) }}</td>
            <td class="ticket-actions">
              <button class="action-button view-button">
                <svg xmlns="http://www.w3.org/2000/svg" class="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                </svg>
              </button>
              <button class="action-button resolve-button" v-if="ticket.status !== 'resolved'">
                <svg xmlns="http://www.w3.org/2000/svg" class="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="tickets-pagination">
      <button class="pagination-button" :disabled="currentPage === 1">
        <svg xmlns="http://www.w3.org/2000/svg" class="pagination-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
      <span class="pagination-info">Page {{ currentPage }} of {{ totalPages }}</span>
      <button class="pagination-button" :disabled="currentPage === totalPages">
        <svg xmlns="http://www.w3.org/2000/svg" class="pagination-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'SupportTicketsSection',
  props: {
    ticketsData: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const priorityFilter = ref('all');
    const statusFilter = ref('all');
    const currentPage = ref(1);
    const itemsPerPage = 5;
    
    // Tickets data either from API or dummy data
    const tickets = computed(() => {
      if (props.ticketsData && props.ticketsData.tickets && props.ticketsData.tickets.length > 0) {
        return props.ticketsData.tickets;
      }
      
      // Dummy tickets data
      return [
        {
          id: 'TKT-1025',
          user: { name: 'John Smith', email: 'john.smith@example.com' },
          subject: 'Cannot access course materials',
          priority: 'high',
          status: 'open',
          created_at: new Date(Date.now() - 8 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1024',
          user: { name: 'Sarah Johnson', email: 'sarah.j@example.com' },
          subject: 'Payment processing issue',
          priority: 'high',
          status: 'in_progress',
          created_at: new Date(Date.now() - 12 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1023',
          user: { name: 'Michael Chen', email: 'mchen@example.com' },
          subject: 'Certificate not generating after completion',
          priority: 'medium',
          status: 'open',
          created_at: new Date(Date.now() - 16 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1022',
          user: { name: 'Emily Davis', email: 'emily.d@example.com' },
          subject: 'Need to reset password',
          priority: 'low',
          status: 'resolved',
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1021',
          user: { name: 'Robert Wilson', email: 'rwilson@example.com' },
          subject: 'Video streaming quality issue',
          priority: 'medium',
          status: 'in_progress',
          created_at: new Date(Date.now() - 36 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1020',
          user: { name: 'Lisa Thompson', email: 'lisa.t@example.com' },
          subject: 'Request for course refund',
          priority: 'high',
          status: 'resolved',
          created_at: new Date(Date.now() - 48 * 60 * 60 * 1000)
        },
        {
          id: 'TKT-1019',
          user: { name: 'David Brown', email: 'dbrown@example.com' },
          subject: 'Assignment submission not working',
          priority: 'medium',
          status: 'resolved',
          created_at: new Date(Date.now() - 72 * 60 * 60 * 1000)
        }
      ];
    });
    
    // Filter tickets based on selected filters
    const filteredTickets = computed(() => {
      let result = tickets.value;
      
      if (priorityFilter.value !== 'all') {
        result = result.filter(ticket => ticket.priority === priorityFilter.value);
      }
      
      if (statusFilter.value !== 'all') {
        result = result.filter(ticket => ticket.status === statusFilter.value);
      }
      
      // Apply pagination
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return result.slice(start, end);
    });
    
    // Calculate total pages for pagination
    const totalPages = computed(() => {
      let filteredCount = tickets.value.length;
      
      if (priorityFilter.value !== 'all') {
        filteredCount = tickets.value.filter(ticket => ticket.priority === priorityFilter.value).length;
      }
      
      if (statusFilter.value !== 'all') {
        filteredCount = filteredCount.filter(ticket => ticket.status === statusFilter.value).length;
      }
      
      return Math.ceil(filteredCount / itemsPerPage);
    });
    
    // Calculate ticket statistics
    const ticketStats = computed(() => {
      if (props.ticketsData && props.ticketsData.statistics) {
        return props.ticketsData.statistics;
      }
      
      // Calculate from dummy data
      const open = tickets.value.filter(t => t.status === 'open').length;
      const inProgress = tickets.value.filter(t => t.status === 'in_progress').length;
      const resolved = tickets.value.filter(t => t.status === 'resolved').length;
      
      return {
        total: tickets.value.length,
        open,
        in_progress: inProgress,
        resolved,
        average_time: 12 // Average response time in hours
      };
    });
    
    // Helper function to format ticket status for display
    const formatStatus = (status) => {
      switch (status) {
        case 'in_progress':
          return 'In Progress';
        default:
          return status.charAt(0).toUpperCase() + status.slice(1);
      }
    };
    
    // Helper function to format date
    const formatDate = (date) => {
      const now = new Date();
      const diff = now - date;
      
      // Less than 24 hours
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours}h ago`;
      }
      
      // Less than 7 days
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return `${days}d ago`;
      }
      
      // Format as date
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return {
      priorityFilter,
      statusFilter,
      currentPage,
      filteredTickets,
      totalPages,
      ticketStats,
      formatStatus,
      formatDate
    };
  }
};
</script>

<style scoped>
.support-tickets-section {
  @apply bg-white rounded-lg shadow p-6;
}

.section-header {
  @apply flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-3 sm:space-y-0;
}

.section-title {
  @apply text-xl font-semibold text-gray-800;
}

.filter-actions {
  @apply flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2;
}

.filter-select {
  @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
  focus:ring-blue-500 focus:border-blue-500 block p-2;
}

.tickets-overview {
  @apply grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6;
}

.ticket-stat-card {
  @apply bg-gray-50 rounded-lg p-4 flex flex-col items-center justify-center;
}

.stat-value {
  @apply text-xl font-bold text-gray-800;
}

.stat-label {
  @apply text-sm text-gray-600;
}

.tickets-table-container {
  @apply overflow-x-auto rounded-lg border border-gray-200 mb-4;
}

.tickets-table {
  @apply min-w-full divide-y divide-gray-200;
}

.tickets-table thead {
  @apply bg-gray-50;
}

.tickets-table thead th {
  @apply py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.ticket-row {
  @apply bg-white border-b;
}

.ticket-row:hover {
  @apply bg-gray-50;
}

.ticket-row td {
  @apply py-3 px-3 text-sm text-gray-800;
}

.ticket-id {
  @apply font-medium text-gray-600;
}

.user-info {
  @apply flex items-center;
}

.user-avatar {
  @apply flex-shrink-0 h-7 w-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium mr-2;
}

.ticket-subject {
  @apply max-w-xs truncate;
}

.priority-badge, .status-badge {
  @apply inline-flex px-2 py-0.5 rounded-full text-xs font-medium;
}

.priority-high {
  @apply bg-red-100 text-red-800;
}

.priority-medium {
  @apply bg-yellow-100 text-yellow-800;
}

.priority-low {
  @apply bg-green-100 text-green-800;
}

.status-open {
  @apply bg-blue-100 text-blue-800;
}

.status-in_progress {
  @apply bg-purple-100 text-purple-800;
}

.status-resolved {
  @apply bg-gray-100 text-gray-800;
}

.ticket-actions {
  @apply flex items-center justify-end space-x-1;
}

.action-button {
  @apply p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500;
}

.resolve-button {
  @apply text-green-500 hover:text-green-700;
}

.action-icon {
  @apply h-4 w-4;
}

.tickets-pagination {
  @apply flex items-center justify-center space-x-4;
}

.pagination-button {
  @apply p-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed;
}

.pagination-icon {
  @apply h-5 w-5;
}

.pagination-info {
  @apply text-sm text-gray-700;
}

@media (prefers-color-scheme: dark) {
  .support-tickets-section {
    @apply bg-gray-800;
  }
  
  .section-title {
    @apply text-gray-100;
  }
  
  .filter-select {
    @apply bg-gray-700 border-gray-600 text-gray-100;
  }
  
  .ticket-stat-card {
    @apply bg-gray-700;
  }
  
  .stat-value {
    @apply text-gray-100;
  }
  
  .stat-label {
    @apply text-gray-400;
  }
  
  .tickets-table-container {
    @apply border-gray-700;
  }
  
  .tickets-table thead {
    @apply bg-gray-700;
  }
  
  .tickets-table thead th {
    @apply text-gray-300;
  }
  
  .ticket-row {
    @apply bg-gray-800 border-gray-700;
  }
  
  .ticket-row:hover {
    @apply bg-gray-700;
  }
  
  .ticket-row td {
    @apply text-gray-300;
  }
  
  .ticket-id {
    @apply text-gray-400;
  }
  
  .user-avatar {
    @apply bg-blue-600;
  }
  
  .action-button {
    @apply text-gray-400 hover:text-gray-200 hover:bg-gray-700;
  }
  
  .pagination-button {
    @apply bg-gray-700 text-gray-300 hover:bg-gray-600;
  }
  
  .pagination-info {
    @apply text-gray-300;
  }
}
</style> 